import { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import API from "../../services/api";

// ─── CONFIG ──────────────────────────────────────────────────────────────────
const PRODUCTS_API = `${process.env.REACT_APP_API_URL}/api/products`;
const PI_API       = `${process.env.REACT_APP_API_URL}/api/pi`;

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const fIN  = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

// ─── DESIGN TOKENS ───────────────────────────────────────────────────────────
const RAJ  = { fontFamily: "'Poppins',sans-serif" };
const MONO = { fontFamily: "'Poppins', sans-serif" };
const SANS = { fontFamily: "'Poppins',sans-serif" };

// ─── STATUS CONFIG ────────────────────────────────────────────────────────────
const STATUS_MAP = {
  draft:     { bg: "rgba(124,109,250,0.1)", border: "rgba(124,109,250,0.3)", text: "#7c6dfa" },
  sent:      { bg: "rgba(64,192,240,0.1)",  border: "rgba(64,192,240,0.3)",  text: "#1a9ec0" },
  confirmed: { bg: "rgba(64,208,152,0.1)",  border: "rgba(64,208,152,0.3)",  text: "#40d098" },
  cancelled: { bg: "rgba(240,80,96,0.1)",   border: "rgba(240,80,96,0.3)",   text: "#f05060" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_MAP[status] || STATUS_MAP.draft;
  return (
    <span className="inline-block px-[8px] py-[2px] rounded-full text-[9px] font-semibold tracking-[1.2px] uppercase"
      style={{ background: s.bg, border: `1px solid ${s.border}`, color: s.text, ...MONO }}>
      {status || "draft"}
    </span>
  );
};

// ─── SKELETON ─────────────────────────────────────────────────────────────────
const Shimmer = ({ w = "100%", h = 12, className = "" }) => (
  <div className={`rounded-[4px] bg-[#e8e8f2] ${className}`}
    style={{ width: w, height: h, animation: "shimmer 1.6s ease-in-out infinite" }} />
);

// ─── STAT CARD ────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, color, icon, loading, onClick }) {
  return (
    <div
      onClick={onClick}
      className={`bg-[#f8f8ff] border border-[#d0d0e8] rounded-xl px-5 py-5 relative overflow-hidden shadow-sm
        ${onClick ? "cursor-pointer hover:border-[#7c6dfa] hover:-translate-y-px transition-all duration-200" : ""}`}
    >
      <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: color }} />
      <div className="flex items-start justify-between mb-3">
        <p className="text-[9.5px] tracking-[2px] uppercase text-[#4a4a75]" style={MONO}>{label}</p>
        <span className="text-[20px] opacity-70">{icon}</span>
      </div>
      {loading
        ? <Shimmer h={28} w="60%" className="mb-2" />
        : <p className="text-[26px] font-bold mb-1" style={{ ...RAJ, color }}>{value}</p>}
      {loading
        ? <Shimmer h={10} w="40%" />
        : sub && <p className="text-[10px] text-[#4a4a75]" style={MONO}>{sub}</p>}
    </div>
  );
}

// ─── RECENT PI ROW ────────────────────────────────────────────────────────────
function RecentPIRow({ pi, i, navigate }) {
  return (
    <tr
      className="border-b border-[rgba(208,208,232,0.6)] hover:bg-[rgba(124,109,250,0.03)] transition-colors cursor-pointer group"
      onClick={() => navigate(`/pi/${pi._id}`)}
      style={{ animation: `fadeUp .2s ease ${i * 0.04}s both` }}
    >
      <td className="px-4 py-[10px]">
        <span className="text-[11px] font-semibold text-[#7c6dfa] group-hover:underline" style={MONO}>
          {pi.piRefNo}
        </span>
      </td>
      <td className="px-4 py-[10px]">
        <p className="text-[12px] font-semibold text-[#1a1a2e]" style={SANS}>{pi.companyName}</p>
        {pi.gstin && <p className="text-[10px] text-[#4a4a75]" style={MONO}>{pi.gstin}</p>}
      </td>
      <td className="px-4 py-[10px] text-[11px] text-[#4a4a75] whitespace-nowrap" style={MONO}>
        {fDate(pi.date || pi.createdAt)}
      </td>
      <td className="px-4 py-[10px] text-right">
        <span className="text-[12px] font-bold text-[#f0c040]" style={MONO}>
          ₹{fIN(pi.grandTotal)}
        </span>
      </td>
      <td className="px-4 py-[10px] text-center">
        <StatusBadge status={pi.status} />
      </td>
    </tr>
  );
}

// ─── MAIN ────────────────────────────────────────────────────────────────────
export default function DashboardHome() {
  const { user }    = useContext(AuthContext);
  const navigate    = useNavigate();

  const [stats, setStats]         = useState(null);
  const [recentPIs, setRecentPIs] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [productsRes, pisRes] = await Promise.all([
        API.get(PRODUCTS_API),
        API.get(`${PI_API}?page=1&limit=50`),
      ]);

      const products = productsRes.data.data || [];
      const allPIs   = pisRes.data.data       || [];
      const total    = pisRes.data.total      || allPIs.length;

      // ── Compute stats ───────────────────────────────────────────────────
      const totalRevenue  = allPIs.reduce((s, p) => s + (p.grandTotal || 0), 0);
      const totalKgs      = allPIs.reduce((s, p) => s + (p.totalKgs   || 0), 0);
      const thisMonth     = new Date();
      const monthPIs      = allPIs.filter(p => {
        const d = new Date(p.createdAt);
        return d.getMonth() === thisMonth.getMonth() && d.getFullYear() === thisMonth.getFullYear();
      });
      const monthRevenue  = monthPIs.reduce((s, p) => s + (p.grandTotal || 0), 0);

      // Status breakdown
      const statusCount = allPIs.reduce((acc, p) => {
        const st = p.status || "draft";
        acc[st] = (acc[st] || 0) + 1;
        return acc;
      }, {});

      // Thickness variants
      const totalVariants = products.reduce((s, p) => s + (p.profileThickness?.length || 0), 0);

      setStats({
        totalPIs: total,
        totalProducts: products.length,
        totalVariants,
        totalRevenue,
        totalKgs,
        monthPIs: monthPIs.length,
        monthRevenue,
        statusCount,
      });

      // Recent 8 PIs
      setRecentPIs(allPIs.slice(0, 8));
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load dashboard data");
      console.error("Dashboard fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const initials = user?.email?.slice(0, 2).toUpperCase() || "U";

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes shimmer { 0%,100%{opacity:.4} 50%{opacity:.9} }
        @keyframes fadeUp  { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin    { to{transform:rotate(360deg)} }
        .anim-fade-up { animation: fadeUp .3s ease both; }
      `}</style>

      <div className="min-h-screen bg-[#f4f4fa]"
        style={{ backgroundImage: "radial-gradient(ellipse 70% 30% at 60% -8%, rgba(124,109,250,0.08), transparent 55%)" }}>
        <div className="max-w-[1400px] mx-auto px-4 py-6 md:px-6 md:py-8">

          {/* ── HEADER ── */}
          <div className="flex items-start justify-between flex-wrap gap-y-3 mb-8 anim-fade-up">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-[14px] flex items-center justify-center text-sm font-bold text-white tracking-wider bg-gradient-to-br from-[#6c63ff] to-[#a855f7] shadow-md flex-shrink-0">
                {initials}
              </div>
              <div>
                <p className="text-[11px] text-[#4a4a75] mb-[2px]" style={MONO}>{greeting},</p>
                <h1 className="text-[24px] font-bold text-[#1a1a2e] leading-none" style={RAJ}>
                  {user?.email?.split("@")[0] || "User"}
                </h1>
                <p className="text-[10px] text-[#6e6e94] mt-[2px]" style={MONO}>
                  {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                </p>
              </div>
            </div>

            <button
              onClick={fetchData}
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-[9px] rounded-[8px] border border-[#b8b8d4] text-[#4a4a75] text-[12px] font-semibold bg-transparent hover:border-[#7c6dfa] hover:text-[#1a1a2e] transition-all disabled:opacity-40"
              style={RAJ}
            >
              {loading
                ? <span className="w-3 h-3 rounded-full border-2 border-[#b8b8d4] border-t-[#7c6dfa] inline-block" style={{ animation: "spin .7s linear infinite" }} />
                : "↺"}
              Refresh
            </button>
          </div>

          {/* ── ERROR BANNER ── */}
          {error && (
            <div className="mb-6 px-5 py-4 bg-[rgba(240,80,96,0.07)] border border-[rgba(240,80,96,0.25)] rounded-xl flex items-center justify-between">
              <p className="text-[12px] text-[#f05060]" style={MONO}>⚠ {error}</p>
              <button onClick={fetchData} className="text-[11px] text-[#f05060] underline cursor-pointer bg-transparent border-0" style={MONO}>Retry</button>
            </div>
          )}

          {/* ── STAT CARDS — ROW 1 ── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5 anim-fade-up" style={{ animationDelay: ".05s" }}>
            <StatCard
              label="Total PIs"
              value={stats?.totalPIs ?? "—"}
              sub={`${stats?.monthPIs ?? 0} this month`}
              color="#7c6dfa"
              icon="📄"
              loading={loading}
              onClick={() => navigate("/dashboard/pi")}
            />
            <StatCard
              label="Total Revenue"
              value={stats ? `₹${fIN(stats.totalRevenue)}` : "—"}
              sub={`₹${fIN(stats?.monthRevenue)} this month`}
              color="#f0c040"
              icon="💰"
              loading={loading}
            />
            <StatCard
              label="Total KGS Sold"
              value={stats ? `${Number(stats.totalKgs).toFixed(0)} kg` : "—"}
              sub="across all PIs"
              color="#40d098"
              icon="⚖️"
              loading={loading}
            />
            <StatCard
              label="Products"
              value={stats?.totalProducts ?? "—"}
              sub={`${stats?.totalVariants ?? 0} thickness variants`}
              color="#1a9ec0"
              icon="📦"
              loading={loading}
              onClick={() => navigate("/dashboard/products")}
            />
          </div>

          {/* ── STAT CARDS — ROW 2 (Status breakdown) ── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 anim-fade-up" style={{ animationDelay: ".1s" }}>
            {[
              { label: "Draft PIs",     key: "draft",     color: "#7c6dfa", icon: "🖊"  },
              { label: "Sent PIs",      key: "sent",      color: "#1a9ec0", icon: "📤" },
              { label: "Confirmed PIs", key: "confirmed", color: "#40d098", icon: "✅" },
              { label: "Cancelled PIs", key: "cancelled", color: "#f05060", icon: "❌" },
            ].map((s) => (
              <StatCard
                key={s.key}
                label={s.label}
                value={stats?.statusCount?.[s.key] ?? 0}
                sub="click to filter"
                color={s.color}
                icon={s.icon}
                loading={loading}
                onClick={() => navigate(`/dashboard/pi?status=${s.key}`)}
              />
            ))}
          </div>

          {/* ── MAIN GRID: Recent PIs + Quick Actions ── */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-5">

            {/* ── RECENT PIs TABLE ── */}
            <div
              className="relative bg-[#f8f8ff] border border-[#d0d0e8] rounded-xl overflow-hidden shadow-sm anim-fade-up"
              style={{ animationDelay: ".14s" }}
            >
              {/* accent bar */}
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#7c6dfa] via-[#40c0f0] to-[#f0c040]" />

              <div className="flex items-center justify-between px-5 py-4 border-b border-[#d0d0e8]">
                <span className="text-[11px] font-semibold tracking-[3px] uppercase text-[#4a4a75]" style={RAJ}>
                  Recent Proforma Invoices
                </span>
                <button
                  onClick={() => navigate("/dashboard/pi")}
                  className="text-[11px] text-[#7c6dfa] hover:underline cursor-pointer bg-transparent border-0 font-semibold"
                  style={MONO}
                >
                  View all →
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse min-w-[560px]">
                  <thead>
                    <tr className="bg-[#eeeef6] border-b border-[#d0d0e8]">
                      {["PI Ref No.", "Company", "Date", "Grand Total", "Status"].map((h) => (
                        <th key={h}
                          className="px-4 py-[10px] text-[9px] tracking-[1.2px] uppercase text-[#4a4a75] font-medium text-left whitespace-nowrap"
                          style={MONO}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading
                      ? Array.from({ length: 6 }).map((_, i) => (
                          <tr key={i} className="border-b border-[rgba(208,208,232,0.6)]">
                            {[100, 140, 80, 90, 70].map((w, j) => (
                              <td key={j} className="px-4 py-[11px]"><Shimmer w={w} /></td>
                            ))}
                          </tr>
                        ))
                      : recentPIs.length === 0
                      ? (
                        <tr>
                          <td colSpan={5} className="text-center py-12">
                            <div className="text-[36px] opacity-20 mb-2">📋</div>
                            <p className="text-[13px] font-semibold text-[#1a1a2e] opacity-30" style={RAJ}>No PIs yet</p>
                            <p className="text-[11px] text-[#4a4a75]" style={MONO}>Create your first PI</p>
                          </td>
                        </tr>
                      )
                      : recentPIs.map((pi, i) => (
                          <RecentPIRow key={pi._id} pi={pi} i={i} navigate={navigate} />
                        ))
                    }
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── RIGHT COLUMN ── */}
            <div className="flex flex-col gap-5">

              {/* Quick Actions */}
              <div
                className="relative bg-[#f8f8ff] border border-[#d0d0e8] rounded-xl overflow-hidden shadow-sm anim-fade-up"
                style={{ animationDelay: ".18s" }}
              >
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#7c6dfa] to-[#40c0f0]" />
                <div className="px-5 py-4 border-b border-[#d0d0e8]">
                  <span className="text-[11px] font-semibold tracking-[3px] uppercase text-[#4a4a75]" style={RAJ}>Quick Actions</span>
                </div>
                <div className="p-4 flex flex-col gap-[8px]">
                  {[
                    { label: "New Proforma Invoice", path: "/dashboard/pi",       icon: "📄", color: "#7c6dfa", bg: "rgba(124,109,250,0.08)", border: "rgba(124,109,250,0.2)"  },
                    { label: "Manage Products",       path: "/dashboard/products", icon: "📦", color: "#1a9ec0", bg: "rgba(64,192,240,0.08)",  border: "rgba(64,192,240,0.2)"  },
                    { label: "View All PIs",          path: "/dashboard/pi",       icon: "📋", color: "#40d098", bg: "rgba(64,208,152,0.08)",  border: "rgba(64,208,152,0.2)"  },
                    { label: "Clients",               path: "/dashboard/clients",  icon: "🏢", color: "#f0c040", bg: "rgba(240,192,64,0.08)",  border: "rgba(240,192,64,0.2)"  },
                  ].map((a) => (
                    <button
                      key={a.label}
                      onClick={() => navigate(a.path)}
                      className="w-full flex items-center gap-3 px-4 py-[11px] rounded-[10px] border cursor-pointer transition-all hover:-translate-y-px hover:shadow-sm bg-transparent text-left"
                      style={{ background: a.bg, borderColor: a.border }}
                    >
                      <span className="text-[18px]">{a.icon}</span>
                      <span className="text-[13px] font-semibold" style={{ ...RAJ, color: a.color }}>{a.label}</span>
                      <span className="ml-auto text-[12px]" style={{ color: a.color, opacity: 0.6 }}>→</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Status breakdown chart */}
              <div
                className="relative bg-[#f8f8ff] border border-[#d0d0e8] rounded-xl overflow-hidden shadow-sm anim-fade-up"
                style={{ animationDelay: ".22s" }}
              >
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#f0c040] to-[#f05060]" />
                <div className="px-5 py-4 border-b border-[#d0d0e8]">
                  <span className="text-[11px] font-semibold tracking-[3px] uppercase text-[#4a4a75]" style={RAJ}>PI Status Breakdown</span>
                </div>
                <div className="p-5 flex flex-col gap-3">
                  {loading
                    ? Array.from({ length: 4 }).map((_, i) => <Shimmer key={i} h={14} />)
                    : Object.entries(STATUS_MAP).map(([key, cfg]) => {
                        const count = stats?.statusCount?.[key] || 0;
                        const total = stats?.totalPIs || 1;
                        const pct   = Math.round((count / total) * 100);
                        return (
                          <div key={key}>
                            <div className="flex items-center justify-between mb-[5px]">
                              <span className="text-[10px] uppercase tracking-[1px] font-semibold" style={{ color: cfg.text, ...MONO }}>{key}</span>
                              <span className="text-[10px] text-[#4a4a75]" style={MONO}>{count} ({pct}%)</span>
                            </div>
                            <div className="w-full h-[6px] bg-[#eeeef6] rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all duration-700"
                                style={{ width: `${pct}%`, background: cfg.text }}
                              />
                            </div>
                          </div>
                        );
                      })}
                </div>
              </div>

              {/* System info */}
              <div
                className="relative bg-[#f8f8ff] border border-[#d0d0e8] rounded-xl overflow-hidden shadow-sm anim-fade-up"
                style={{ animationDelay: ".26s" }}
              >
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#40d098] to-[#1a9ec0]" />
                <div className="px-5 py-4 border-b border-[#d0d0e8]">
                  <span className="text-[11px] font-semibold tracking-[3px] uppercase text-[#4a4a75]" style={RAJ}>System Info</span>
                </div>
                <div className="p-5 flex flex-col gap-3">
                  {[
                    { label: "Logged in as", val: user?.email },
                    { label: "Role",         val: user?.role?.toUpperCase() },
                    { label: "HSN Code",     val: "72255010" },
                    { label: "IGST Rate",    val: "18%" },
                  ].map((r) => (
                    <div key={r.label} className="flex items-center justify-between">
                      <span className="text-[10px] text-[#4a4a75]" style={MONO}>{r.label}</span>
                      <span className="text-[10px] font-semibold text-[#1a1a2e] truncate max-w-[140px] text-right" style={MONO}>{r.val || "—"}</span>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
}